import {
  DateInput,
  ErrorMessage,
  SelectButton,
  SelectButtonBaseProps,
} from '@hc-frontend/core-ui';
import { getISODate, isoStringDateToDate } from '@hc-frontend/core-utils-date';
import { ChangeEvent, ChangeEventHandler, FormEvent, useState } from 'react';
import { z } from 'zod';

import { DOB, DOB_SCHEMA, TOBACCO, TOBACCO_SCHEMA } from './constants';
import { FormButtons } from './form-buttons';
import { useSubsidyCalculator } from './provider';

const FormSchema = z.object({
  dob: DOB_SCHEMA,
  tobacco: TOBACCO_SCHEMA,
});

export function ProfileStep() {
  const { setStep } = useSubsidyCalculator();
  const { answers, setAnswer } = useSubsidyCalculator();

  const [dob, setDob] = useState<Date | undefined>(
    answers[DOB] !== undefined ? isoStringDateToDate(answers[DOB]) : undefined,
  );

  const [tobacco, setTobacco] = useState<string | undefined>(
    answers[TOBACCO] !== undefined ? String(answers[TOBACCO]) : undefined,
  );

  const [dobError, setDobError] = useState<string | null>(null);
  const [tobaccoError, setTobaccoError] = useState<string | null>(null);

  const tobaccoOptions: SelectButtonBaseProps['options'] = [
    {
      id: '0',
      label: 'No',
      value: '0',
      checked: '0' === tobacco,
    },
    {
      id: '1',
      label: 'Yes',
      value: '1',
      checked: '1' === tobacco,
    },
  ];

  const handleTobaccoChange: ChangeEventHandler<HTMLInputElement> = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setTobacco(value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setDobError(null);
    setTobaccoError(null);

    const result = FormSchema.safeParse({
      dob: dob !== undefined ? getISODate(dob) : undefined,
      tobacco: tobacco !== undefined ? Number(tobacco) : undefined,
    });

    if (result.success) {
      setAnswer(DOB, result.data.dob);
      setAnswer(TOBACCO, result.data.tobacco);
      setStep((v) => v + 1);
    } else {
      const fields = result.error.format();

      if (fields.dob) {
        setDobError('Please enter a valid date.');
      }
      if (fields.tobacco) {
        setTobaccoError('Please select an option.');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="grid grid-cols-1 gap-16-16">
        <DateInput
          className="max-w-none animate-in fade-in slide-in-from-bottom-8-8 duration-1000 fill-mode-both"
          name={DOB}
          onChange={(v) => setDob(v)}
          initialValue={dob}
          label="Date of Birth"
          errorText={dobError ? <ErrorMessage>{dobError}</ErrorMessage> : null}
          helpText="This improves the accuracy of your subsidy calculation."
        />

        <SelectButton
          name={TOBACCO}
          className="max-w-none animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-150 fill-mode-both"
          onChange={handleTobaccoChange}
          errorText={
            tobaccoError ? <ErrorMessage>{tobaccoError}</ErrorMessage> : null
          }
          options={tobaccoOptions}
          label="Tobacco Use"
        />
      </div>

      <FormButtons
        showBack
        className="animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-300 fill-mode-both"
      />
    </form>
  );
}
